import React, { type ChangeEvent, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SelectOptionGroup } from '../../../config/types';
import { validateField } from '../../../utils/form-utils';
import ErrorMessage from '../ErrorMessage';
import { DefaultCheckbox } from './DefaultCheckbox';
import type { CheckboxGroupProps, CheckboxSubGroupProps } from './Props';

const ConditionalCheckboxSubGroup = ({
  id,
  anchorId,
  currentValue,
  option,
  name,
  disabled,
  handleOnChange,
}: CheckboxSubGroupProps) => (
  <div className="govuk-radios fieldset-container" data-module="govuk-radios" key={option.label}>
    <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
      <h3 className="govuk-fieldset__heading">{option.label}</h3>
    </legend>
    {option.options.map((option, index) => (
      <DefaultCheckbox
        id={id}
        name={name}
        disabled={disabled}
        key={option}
        anchorId={anchorId}
        index={index}
        item={option as string}
        currentValue={currentValue}
        handleOnChange={handleOnChange}
      />
    ))}
  </div>
);

export default function ConditionalCheckboxField({
  anchorId,
  question,
  disabled,
  id,
}: CheckboxGroupProps) {
  const { options, name, title, rules, errorMessages } = question;
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const [currentValue, setCurrentValue] = useState(getValues(name) ?? []);

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>, option: string) => {
    const newValues = e.target.checked
      ? [...currentValue, option]
      : currentValue.filter((v: string) => v !== option);

    setCurrentValue(newValues);
    setValue(name, newValues);
  };

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, []);
    }
  }, [getValues, name, setValue]);

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, rules || {}, value, errorMessages),
  });
  const { error } = getFieldState(name);

  // If the options contains a SubGroup they must all be subgroups
  const optionIsSubGroup: boolean =
    (options && typeof options[0] === 'object' && 'label' in options[0]) || false;

  return (
    <>
      {error && <ErrorMessage error={error} />}
      <div className="govuk-checkboxes" data-module="govuk-checkboxes">
        {options?.map((option, i) => (
          <React.Fragment key={(option as SelectOptionGroup).label}>
            {optionIsSubGroup && (
              <ConditionalCheckboxSubGroup
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                anchorId={anchorId}
                option={option as SelectOptionGroup}
                currentValue={currentValue}
                handleOnChange={handleOnChange}
              />
            )}
            {!optionIsSubGroup && (
              <DefaultCheckbox
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                anchorId={anchorId}
                index={i}
                item={option as string}
                currentValue={currentValue}
                handleOnChange={handleOnChange}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}
